import { useState, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { CalendarIcon, MagnifyingGlassIcon, ChatBubbleLeftRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/layout/PageHeader';
import { useCollaborations } from '../hooks/useCollaborations';
import { useCollaborationConfigs } from '../hooks/useCollaborationConfigs';
import { useEndpoint } from '../hooks/useEndpoints';
import { useListState } from '../hooks/useListState';
import { useSortableData } from '../hooks/useSortableData';
import { SearchBar } from '../components/ui/SearchBar';
import { Pagination } from '../components/ui/Pagination';
import { ViewToggle } from '../components/ui/ViewToggle';
import { DataTable, DataTableHead, DataTableBody, DataTableRow, DataTableCell, TableHeader } from '../components/ui/DataTable';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import { EmptyState } from '../components/shared/EmptyState';
import CollaborationCard from '../components/collaborations/CollaborationCard';
import { formatRelativeTime } from '../utils/dateUtils';
import { endpointDisplayName } from '../utils/endpointLabel';

/**
 * Drill-down filters read from the URL (task 1281). The Analytics tab links a
 * chart day here as `?endpointId=&from=&to=[&environment=][&status=failed]`;
 * the service reads the same days and environment the series does.
 */
const URL_FILTERS = ['endpointId', 'from', 'to', 'environment', 'status'] as const;
type UrlFilter = (typeof URL_FILTERS)[number];

/** The day or span chip's text */
function rangeLabel(from: string, to: string): string {
  if (from && to) return from === to ? `Day: ${from}` : `${from} to ${to}`;
  if (from) return `From ${from}`;
  return `Until ${to}`;
}

function formatDuration(startedAt: string | null, completedAt: string | null): string {
  if (!startedAt) return '-';
  if (!completedAt) return 'Running...';
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 1000) return '< 1s';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

export default function CollaborationsPage() {
  const navigate = useNavigate();
  const { page, limit, search, viewMode, setPage, setLimit, setSearch, setViewMode } = useListState({
    entityType: 'collaborations',
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const endpointIdFilter = searchParams.get('endpointId') ?? '';
  const fromFilter = searchParams.get('from') ?? '';
  const toFilter = searchParams.get('to') ?? '';
  const environmentFilter = searchParams.get('environment') ?? '';
  const statusFilter = searchParams.get('status') ?? '';
  const [configFilter, setConfigFilter] = useState('');

  /** Set or clear URL filters; the rest of the query string is kept */
  const updateUrlFilters = useCallback((changes: Partial<Record<UrlFilter, string>>) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      for (const [key, value] of Object.entries(changes)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      return next;
    });
    setPage(1);
  }, [setSearchParams, setPage]);

  const handleStatusFilterChange = useCallback((value: string) => {
    updateUrlFilters({ status: value });
  }, [updateUrlFilters]);

  // The AI Service chip shows the service's name, not its id
  const { data: filterEndpoint } = useEndpoint(endpointIdFilter || undefined);

  const handleConfigFilterChange = useCallback((value: string) => {
    setConfigFilter(value);
    setPage(1);
  }, [setPage]);

  // Fetch configs for the filter dropdown
  const { data: configsData } = useCollaborationConfigs(1, 100);
  const configs = configsData?.data || [];

  const { data, isLoading, error } = useCollaborations(page, limit, {
    search: search || undefined,
    status: statusFilter || undefined,
    configId: configFilter || undefined,
    endpointId: endpointIdFilter || undefined,
    from: fromFilter || undefined,
    to: toFilter || undefined,
    environment: environmentFilter || undefined,
  });
  const collaborations = data?.data || [];
  const pagination = data?.pagination;

  const { sortedData, sortConfig, handleSort } = useSortableData(collaborations, 'updatedAt', 'desc');

  // Delete state

  const hasActiveFilters = !!(search || statusFilter || configFilter || endpointIdFilter || fromFilter || toFilter || environmentFilter);

  const clearFilters = () => {
    setSearch('');
    setConfigFilter('');
    updateUrlFilters({ endpointId: '', from: '', to: '', environment: '', status: '' });
  };

  const filterChips: Array<{ key: string; label: string; clear: Partial<Record<UrlFilter, string>> }> = [];
  if (endpointIdFilter) {
    filterChips.push({
      key: 'endpoint',
      label: `AI Service: ${filterEndpoint ? endpointDisplayName(filterEndpoint) : endpointIdFilter.slice(0, 8)}`,
      clear: { endpointId: '' },
    });
  }
  if (fromFilter || toFilter) {
    filterChips.push({ key: 'range', label: rangeLabel(fromFilter, toFilter), clear: { from: '', to: '' } });
  }
  if (environmentFilter) {
    filterChips.push({ key: 'environment', label: `Environment: ${environmentFilter}`, clear: { environment: '' } });
  }
  if (statusFilter) {
    filterChips.push({ key: 'status', label: `Status: ${statusFilter}`, clear: { status: '' } });
  }

  const getStatusBadgeClass = (status: string | null) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
      case 'running':
        return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
      case 'failed':
        return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
      case 'skipped':
        return 'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200';
      case 'waiting':
      case 'waiting_for_input':
        return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
      case 'pending':
      default:
        return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
    }
  };

  const getMethodBadgeClass = (method: string) => {
    switch (method) {
      case 'GET':
        return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
      case 'POST':
        return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
      case 'PUT':
        return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
      case 'PATCH':
        return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
      case 'DELETE':
        return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500 dark:text-red-400">Error loading collaborations. Please try again.</div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Collaborations"
        description={pagination ? `${pagination.total} ${pagination.total === 1 ? 'collaboration' : 'collaborations'}${search ? ' found' : ' total'}` : 'Multi-agent execution instances with journal and status tracking'}
      />

      <div className="mt-6">
        {/* Search and Filter Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <SearchBar
                value={search}
                onChange={setSearch}
                placeholder="Search by collaboration ID or request ID..."
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="running">Running</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="skipped">Skipped</option>
              <option value="waiting_for_input">Waiting</option>
            </select>
            <select
              value={configFilter}
              onChange={(e) => handleConfigFilterChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="">All Configs</option>
              {configs.map((config) => (
                <option key={config.id} value={config.id}>{config.name}</option>
              ))}
            </select>
            <ViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />
          </div>
          {filterChips.length > 0 && (
            <ul aria-label="Active filters" className="mt-3 flex flex-wrap items-center gap-2">
              {filterChips.map((chip) => (
                <li
                  key={chip.key}
                  className="inline-flex items-center gap-1 rounded-full bg-indigo-50 dark:bg-indigo-900/40 px-3 py-1 text-sm text-indigo-800 dark:text-indigo-200"
                >
                  <span>{chip.label}</span>
                  <button
                    type="button"
                    onClick={() => updateUrlFilters(chip.clear)}
                    aria-label={`Clear ${chip.label}`}
                    className="rounded-full p-0.5 hover:bg-indigo-100 dark:hover:bg-indigo-800 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500 dark:text-gray-400">Loading collaborations...</div>
          </div>
        ) : sortedData.length === 0 ? (
          hasActiveFilters ? (
            <EmptyState
              icon={<MagnifyingGlassIcon className="h-12 w-12" />}
              title="No matching collaborations"
              description="No collaborations match your current filters. Try adjusting your search or filter criteria."
              action={{
                label: 'Clear Filters',
                onClick: clearFilters,
                variant: 'secondary'
              }}
            />
          ) : (
            <EmptyState
              icon={<ChatBubbleLeftRightIcon className="h-12 w-12" />}
              title="No collaborations yet"
              description="Collaborations are created when you invoke an AI Service. Open one of your AI Services and run it from its Testing tab."
              action={{
                label: 'Go to AI Services',
                onClick: () => navigate('/endpoints'),
                variant: 'primary'
              }}
            />
          )
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {sortedData.map((collaboration) => (
              <CollaborationCard key={collaboration.id} collaboration={collaboration} />
            ))}
          </div>
        ) : (
          <DataTable>
            <DataTableHead>
              <SortableTableHeader
                label="ID"
                field="id"
                sortField={sortConfig.field}
                sortDirection={sortConfig.direction}
                onSort={handleSort}
                className="w-28"
              />
              <TableHeader className="w-24">Status</TableHeader>
              <TableHeader className="w-40">Endpoint</TableHeader>
              <TableHeader className="w-32">Config</TableHeader>
              <TableHeader className="w-24">Duration</TableHeader>
              <SortableTableHeader
                label="Updated"
                field="updatedAt"
                sortField={sortConfig.field}
                sortDirection={sortConfig.direction}
                onSort={handleSort}
                className="w-32"
              />
            </DataTableHead>
            <DataTableBody>
              {sortedData.map((collaboration) => {
                const endpoint = collaboration.endpointRequest?.endpoint;
                return (
                  <DataTableRow
                    key={collaboration.id}
                    onClick={() => navigate(`/collaborations/${collaboration.id}`)}
                  >
                    <DataTableCell className="text-sm font-mono text-gray-600 dark:text-gray-300">
                      {collaboration.id.slice(0, 8)}...
                    </DataTableCell>
                    <DataTableCell>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(collaboration.status)}`}>
                        {collaboration.status}
                      </span>
                    </DataTableCell>
                    <DataTableCell>
                      {endpoint ? (
                        <div className="flex items-center gap-1.5">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getMethodBadgeClass(endpoint.method)}`}>
                            {endpoint.method}
                          </span>
                          <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                            {endpointDisplayName(endpoint)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                      )}
                    </DataTableCell>
                    <DataTableCell>
                      {collaboration.collaborationConfig ? (
                        <Link
                          to={`/configs/${collaboration.collaborationConfig.id}`}
                          className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {collaboration.collaborationConfig.name}
                        </Link>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                      )}
                    </DataTableCell>
                    <DataTableCell className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDuration(collaboration.startedAt, collaboration.completedAt)}
                    </DataTableCell>
                    <DataTableCell className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-1" />
                        {formatRelativeTime(collaboration.updatedAt)}
                      </div>
                    </DataTableCell>
                  </DataTableRow>
                );
              })}
            </DataTableBody>
          </DataTable>
        )}

        {pagination && pagination.totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              pagination={pagination}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
          </div>
        )}
      </div>
    </div>
  );
}
